import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import axios from "axios";
import { FiCheckCircle, FiEdit2, FiLayers } from "react-icons/fi";
import moment from "moment";

import PageHeader from "../../components/ui/PageHeader";
import priceTypes from "../../const/priceTypes";

/**
 * Telegram bot foydalanuvchilari.
 *
 * Bu yerdan uch narsa boshqariladi:
 *   1. kimga botdan foydalanish ruxsati berilgan;
 *   2. qaysi narx turini ko'radi;
 *   3. qaysi omborlarning qoldig'i ko'rinadi — diller o'z omborini ko'rishi
 *      kerak, u esa Alight xodimlari uchun yashirilgan bo'lishi mumkin.
 */

const API = process.env.REACT_APP_API;
const auth = () => ({ headers: { Authorization: localStorage.getItem("token") } });

/** Botda foydalana oladigan turlar (backenddagi BotUserType.isAccess bilan mos). */
const BOT_TYPES = [
  { value: "GUEST", label: "Ruxsat yo'q" },
  { value: "USER", label: "Xodim" },
  { value: "CLIENT", label: "Mijoz" },
  { value: "EMPLOYEE", label: "Xizmatchi" },
  { value: "ADMIN", label: "Admin" },
  { value: "REGIONAL_OPERATOR", label: "Hududiy operator" },
  { value: "FRANCHISING", label: "Franchayzing" },
];

const typeLabel = (v) => BOT_TYPES.find((t) => t.value === v)?.label || v || "—";
const priceLabel = (v) => priceTypes.find((p) => p.en === v)?.uz || v || "—";

const TelegramBotPage = () => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [note, setNote] = useState(null);

  const [warehouses, setWarehouses] = useState([]);
  const [edit, setEdit] = useState(null); // tahrirlanayotgan foydalanuvchi

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [users, whs] = await Promise.all([
        axios.get(`${API}/telegram/users?page=${page}&size=${rowsPerPage}&desc=true`, auth()),
        axios.get(`${API}/warehouse`, auth()),
      ]);
      setRows(users.data.data || []);
      setTotal(users.data.totalElements || 0);
      setWarehouses(whs.data || []);
      setError(null);
    } catch {
      setError("Ro'yxatni olib bo'lmadi.");
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage]);

  useEffect(() => {
    load();
  }, [load]);

  const pending = useMemo(() => rows.filter((r) => r.type === "GUEST"), [rows]);
  const fromLegacy = useMemo(
    () => pending.filter((r) => (r.settings || "").includes("source = legacy")),
    [pending]
  );

  const save = async (user, { type, priceType, warehouseIds }) => {
    try {
      if (type !== user.type || priceType !== user.priceType) {
        await axios.post(
          `${API}/telegram/access/${user.id}?type=${type}&priceType=${priceType}`,
          null,
          auth()
        );
      }
      const current = (user.warehouses || []).map((w) => w.id).sort().join(",");
      if (warehouseIds.slice().sort().join(",") !== current) {
        const q = warehouseIds.length ? `?ids=${warehouseIds.join(",")}` : "";
        await axios.patch(`${API}/telegram/users/${user.id}/warehouses${q}`, null, auth());
      }
      setEdit(null);
      setNote("Saqlandi");
      load();
    } catch (err) {
      setError(err.response?.status === 403 ? "Huquqingiz yetmaydi." : "Saqlab bo'lmadi.");
    }
  };

  const approveLegacy = async () => {
    try {
      const { data } = await axios.post(
        `${API}/telegram/access/bulk?type=USER&onlyLegacy=true`,
        null,
        auth()
      );
      setNote(`${data.approved} ta foydalanuvchiga ruxsat berildi`);
      load();
    } catch {
      setError("Ruxsat berib bo'lmadi.");
    }
  };

  return (
    <Box>
      <PageHeader
        title="Telegram bot"
        subtitle="Botga /start bosgan foydalanuvchilar. Ruxsat bermaguningizcha ular botdan foydalana olmaydi."
        actions={
          fromLegacy.length > 0 ? (
            <Button variant="contained" startIcon={<FiCheckCircle />} onClick={approveLegacy}>
              Eski botdan kelgan {fromLegacy.length} ta kishiga ruxsat berish
            </Button>
          ) : null
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {note && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNote(null)}>
          {note}
        </Alert>
      )}
      {pending.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <b>{pending.length}</b> ta foydalanuvchi ruxsat kutmoqda.
          {fromLegacy.length > 0 && ` Shundan ${fromLegacy.length} tasi eski botdan ko'chib kelgan.`}
        </Alert>
      )}

      <Paper sx={{ overflow: "hidden" }}>
        {loading ? (
          <Box sx={{ display: "grid", placeItems: "center", py: 8 }}>
            <CircularProgress size={24} />
          </Box>
        ) : rows.length === 0 ? (
          <Box sx={{ py: 8, textAlign: "center" }}>
            <Typography variant="h4">Hali hech kim yo'q</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Kimdir botga <b>/start</b> yuborsa, shu yerda paydo bo'ladi.
            </Typography>
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>F.I.SH</TableCell>
                    <TableCell>Boshlagan vaqti</TableCell>
                    <TableCell>Roli</TableCell>
                    <TableCell>Narx turi</TableCell>
                    <TableCell>Ko'radigan omborlar</TableCell>
                    <TableCell align="right" width={90}>
                      Amal
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((u) => {
                    const legacy = (u.settings || "").includes("source = legacy");
                    const own = u.warehouses || [];
                    return (
                      <TableRow key={u.id} hover>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            {u.username ? (
                              <Link
                                href={`https://t.me/${u.username}`}
                                target="_blank"
                                rel="noreferrer"
                                underline="hover"
                              >
                                {u.name}
                              </Link>
                            ) : (
                              <Typography variant="body2">{u.name}</Typography>
                            )}
                            {legacy && (
                              <Tooltip title="Eski botdan ko'chib kelgan">
                                <Chip size="small" label="eski bot" variant="outlined" />
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {moment(u.createdAt).format("DD.MM.YYYY HH:mm")}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={typeLabel(u.type)}
                            color={u.type === "GUEST" ? "warning" : "success"}
                            variant={u.type === "GUEST" ? "outlined" : "filled"}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{priceLabel(u.priceType)}</Typography>
                        </TableCell>
                        <TableCell>
                          {own.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                              hammasi (umumiy qoida)
                            </Typography>
                          ) : (
                            <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                              {own.map((w) => (
                                <Chip key={w.id} size="small" label={w.displayName || w.name} />
                              ))}
                            </Box>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="O'zgartirish">
                            <Button size="small" onClick={() => setEdit(u)} sx={{ minWidth: 0 }}>
                              <FiEdit2 />
                            </Button>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              rowsPerPageOptions={[25, 50, 100]}
              count={total}
              rowsPerPage={rowsPerPage}
              page={page}
              labelRowsPerPage="Qatorlar soni:"
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} / ${count === -1 ? `${to} dan ko'p` : count}`
              }
              onPageChange={(_, p) => setPage(p)}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(+e.target.value);
                setPage(0);
              }}
            />
          </>
        )}
      </Paper>

      {edit && (
        <EditDialog
          user={edit}
          warehouses={warehouses}
          onClose={() => setEdit(null)}
          onSave={(v) => save(edit, v)}
        />
      )}
    </Box>
  );
};

const EditDialog = ({ user, warehouses, onClose, onSave }) => {
  const [type, setType] = useState(user.type || "GUEST");
  const [priceType, setPriceType] = useState(user.priceType || "WHOLESALE");
  const [ids, setIds] = useState((user.warehouses || []).map((w) => w.id));

  const toggle = (id) =>
    setIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 600 }}>
        {user.name}
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: "flex", gap: 2, mb: 3, mt: 1 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Roli</InputLabel>
            <Select value={type} label="Roli" onChange={(e) => setType(e.target.value)}>
              {BOT_TYPES.map((t) => (
                <MenuItem key={t.value} value={t.value}>
                  {t.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small">
            <InputLabel>Narx turi</InputLabel>
            <Select
              value={priceType}
              label="Narx turi"
              onChange={(e) => setPriceType(e.target.value)}
            >
              {priceTypes.map((p) => (
                <MenuItem key={p.en} value={p.en}>
                  {p.uz}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
          <FiLayers />
          <Typography variant="h5">Ko'radigan omborlar</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Hech biri belgilanmasa, umumiy qoida ishlaydi — yashirilmagan omborlarning
          hammasi ko'rinadi. Belgilansa, <b>aynan shular</b> ko'rinadi.
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column" }}>
          {warehouses.map((w) => (
            <FormControlLabel
              key={w.id}
              control={
                <Checkbox
                  size="small"
                  checked={ids.includes(w.id)}
                  onChange={() => toggle(w.id)}
                />
              }
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="body2">{w.displayName || w.name}</Typography>
                  {w.hidden && <Chip size="small" label="umumiy ro'yxatda yashirin" variant="outlined" />}
                </Box>
              }
            />
          ))}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Bekor qilish</Button>
        <Button variant="contained" onClick={() => onSave({ type, priceType, warehouseIds: ids })}>
          Saqlash
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TelegramBotPage;
